<template>
  <div class="roles-main">
    <el-card class="box-card" shadow="never">
      <template #header>
        <div class="clearfix">
          <span class="role-span">{{ $t('fields.siteMessage') }}</span>
        </div>
      </template>
      <div style="margin: 0 20px;">
        <el-row :gutter="20" style="gap:10px;">
          <el-col :xl="4" :lg="6" :md="6" :sm="5">
            <el-select
              v-model="request.status"
              class="input-small"
              :placeholder="t('fields.status')"
              size="normal"
              clearable
            >
              <el-option :label="t('fields.readed')" :value="true" />
              <el-option :label="t('fields.unread')" :value="false" />
            </el-select>
          </el-col>
          <el-col :xl="16" :lg="12" :md="18" :sm="18">
            <el-button
              icon="el-icon-search"
              type="primary"
              @click="loadSiteMessage()"
              size="normal"
            >
              {{ $t('fields.search') }}
            </el-button>
            <el-button size="normal" type="primary" plain @click="resetQuery()">
              {{ $t('fields.reset') }}
            </el-button>
            <el-button
              icon="el-icon-edit"
              size="normal"
              type="success"
              :disabled="uiControl.editBtn"
              @click="readSiteMessage()"
            >
              {{ t('fields.bulk_read') }}
            </el-button>
            <el-button
              icon="el-icon-remove"
              size="normal"
              type="danger"
              :disabled="uiControl.removeBtn"
              @click="removeSiteMessage()"
            >
              {{ t('fields.bulk_delete') }}
            </el-button>
          </el-col>
        </el-row>

        <el-dialog
          v-model="uiControl.dialogVisible"
          @close="handleClose"
          append-to-body
          :width="dialogWidth"
        >
          <!-- title and content are html from backend -->
          <template #title>
            <div class="dialog-title" v-html="uiControl.dialogTitle" />
          </template>
          <div class="dialog-content" v-html="uiControl.dialogContent" />
          <template #footer v-if="showRedirect">
            <el-button type="primary" @click="handleRedirect">
              {{ uiControl.redirectButton }}
            </el-button>
          </template>
        </el-dialog>

        <el-table
          :data="page.records"
          ref="table"
          row-key="id"
          size="normal"
          highlight-current-row
          v-loading="page.loading"
          style="margin-top: 15px; margin-left: 15px;"
          @selection-change="handleSelectionChange"
          @row-click="handleRowClick"
        >
          <template #empty>
            <emptyComp />
          </template>
          <el-table-column type="selection" width="55" />
          <el-table-column
            prop="title"
            :label="t('fields.title')"
            align="center"
            width="200"
          >
            <template #default="scope">
              {{ stripHtml(scope.row.title) }}
            </template>
          </el-table-column>

          <el-table-column
            :label="t('fields.status')"
            align="right"
            width="200"
          >
            <template #default="scope">
              <span v-if="scope.row.status">{{ t('fields.readed') }}</span>
              <span v-else>{{ t('fields.unread') }}</span>
            </template>
          </el-table-column>

          <el-table-column
            prop="sendTime"
            :label="t('fields.createTime')"
            align="center"
          />

          <el-table-column :label="t('fields.operate')" align="left">
            <template #default="scope">
              <el-button
                icon="el-icon-delete"
                size="normal"
                type="delete"
                @click.stop="removeSiteMessage(scope.row)"
              />
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          class="pagination"
          @current-change="changePage"
          layout="prev, pager, next"
          :page-size="request.size"
          :page-count="page.pages"
          :current-page="request.current"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, reactive, ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getAffSiteMessage,
  readAffSiteMessage,
  readMultipleAffSiteMessage,
  deleteAffSiteMessage,
  deleteMultipleAffSiteMessage,
} from '../../../api/affiliate-site-message'
import { ElMessage, ElMessageBox } from 'element-plus'
import emptyComp from '@/components/empty'
import { siteMessageStore } from '@/store/site-message'

const { t } = useI18n()
const unreadStore = siteMessageStore()

const request = reactive({
  status: null,
  size: 20,
  current: 1,
})

const page = reactive({
  pages: 0,
  records: [],
  loading: false,
})

const uiControl = reactive({
  dialogVisible: false,
  dialogTitle: '',
  dialogContent: '',
  redirectType: null,
  redirectUrl: null,
  redirectButton: null,
  editBtn: true,
  removeBtn: true,
})

const isMobile = ref(window.innerWidth < 620)
const dialogWidth = computed(() => {
  return isMobile.value ? '92%' : '580px'
})

function handleResize() {
  isMobile.value = window.innerWidth < 620
}

// plain text for table cells, title may contain html tags
function stripHtml(html) {
  if (!html) {
    return ''
  }
  const doc = new DOMParser().parseFromString(html, 'text/html')
  return doc.body.textContent || ''
}

let chooseSiteMessage = []
function handleSelectionChange(val) {
  chooseSiteMessage = val
  if (chooseSiteMessage.length === 0) {
    uiControl.editBtn = true
    uiControl.removeBtn = true
  } else {
    uiControl.editBtn = false
    uiControl.removeBtn = false
  }
}

// only INNER+OUTER redirect with both url and button text is supported
const showRedirect = computed(() => {
  return (
    (uiControl.redirectType === 'INNER' ||
      uiControl.redirectType === 'OUTER') &&
    !!uiControl.redirectUrl &&
    !!uiControl.redirectButton
  )
})

function handleRedirect() {
  if (uiControl.redirectType === 'INNER') {
    window.location.href = uiControl.redirectUrl
  } else {
    window.open(uiControl.redirectUrl, '_blank')
  }
}

function handleClose() {
  uiControl.dialogVisible = false
}

function handleRowClick(row) {
  readSiteMessage(row)
}

async function readSiteMessage(siteMessage) {
  if (siteMessage) {
    // inbox list already carries the content, no details api
    uiControl.dialogTitle = siteMessage.title
    uiControl.dialogContent = siteMessage.content
    uiControl.redirectType = siteMessage.redirectType
    uiControl.redirectUrl = siteMessage.redirectUrl
    uiControl.redirectButton = siteMessage.redirectButton
    uiControl.dialogVisible = true
    if (siteMessage.status) {
      return
    }
    await readAffSiteMessage(siteMessage.id)
  } else {
    await readMultipleAffSiteMessage(chooseSiteMessage.map(m => m.id))
    ElMessage({ message: t('fields.bulk_read'), type: 'success' })
  }
  await loadSiteMessage()
  unreadStore.loadUnreadCount()
}

function removeSiteMessage(siteMessage) {
  ElMessageBox.confirm(t('message.confirmDelete'), {
    confirmButtonText: t('fields.confirm'),
    cancelButtonText: t('fields.cancel'),
    type: 'warning',
  }).then(async () => {
    if (siteMessage) {
      await deleteAffSiteMessage(siteMessage.id)
    } else {
      await deleteMultipleAffSiteMessage(chooseSiteMessage.map(m => m.id))
    }
    await loadSiteMessage()
    unreadStore.loadUnreadCount()
    ElMessage({ message: t('message.deleteSuccess'), type: 'success' })
  })
}

function resetQuery() {
  request.status = null
  request.current = 1
  loadSiteMessage()
}

async function loadSiteMessage() {
  page.loading = true
  const requestCopy = { ...request }
  const query = {}
  Object.entries(requestCopy).forEach(([key, value]) => {
    // status=false (unread) is a valid filter, only skip empty values
    if (value !== null && value !== '') {
      query[key] = value
    }
  })

  try {
    const { data: ret } = await getAffSiteMessage(query)
    page.pages = ret.pages
    page.records = ret.records
  } finally {
    page.loading = false
  }
}

function changePage(page) {
  if (request.current >= 1) {
    request.current = page
    loadSiteMessage()
  }
}

onMounted(() => {
  loadSiteMessage()
  window.addEventListener('resize', handleResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.box-card {
  ::v-deep(.el-card__body) {
    padding: 0;
  }
}
.el-button {
  margin-bottom: 10px;
}

.dialog-title {
  font-size: 18px;
  line-height: 1.4;
  word-break: break-word;
  padding-right: 20px;
  :deep(p) {
    margin: 0;
  }
}

.dialog-content {
  max-height: 60vh;
  overflow-y: auto;
  word-break: break-word;
  line-height: 1.6;
  :deep(img),
  :deep(video),
  :deep(iframe) {
    max-width: 100%;
    height: auto;
  }
  :deep(table) {
    display: block;
    max-width: 100%;
    overflow-x: auto;
  }
}
</style>
